import type { PensionOptions } from '../../types/tax';

// 2024/2025 Pension/Retirement Fund contribution limits
export const PENSION_LIMITS = {
  maxPercentage: 27.5, // Maximum 27.5% of remuneration or taxable income
  maxAnnualAmount: 350000, // Maximum R350,000 annual contribution
} as const;

export interface PensionCalculationResult {
  contributionAmount: number;
  taxDeductibleAmount: number;
  isAtLimit: boolean;
  limitType?: 'percentage' | 'amount';
  remainingCapacity: number;
}

/**
 * Calculate pension/retirement fund contributions and tax deductions
 * Based on SARS 2024/2025 regulations
 * 
 * @param grossIncome Annual gross income
 * @param options Pension contribution options
 * @returns Pension calculation result
 */
export function calculatePensionContribution(
  grossIncome: number,
  options: PensionOptions
): PensionCalculationResult {
  if (!options.hasPension) {
    return {
      contributionAmount: 0,
      taxDeductibleAmount: 0,
      isAtLimit: false,
      remainingCapacity: Math.min(
        grossIncome * (PENSION_LIMITS.maxPercentage / 100),
        PENSION_LIMITS.maxAnnualAmount
      ),
    };
  }

  let contributionAmount = 0;

  // Calculate contribution based on type
  if (options.contributionType === 'percentage') {
    contributionAmount = grossIncome * (options.contributionAmount / 100);
  } else {
    contributionAmount = options.contributionAmount;
  }

  // Calculate maximum allowable deduction
  const maxPercentageLimit = grossIncome * (PENSION_LIMITS.maxPercentage / 100);
  const maxAllowableDeduction = Math.min(maxPercentageLimit, PENSION_LIMITS.maxAnnualAmount);

  // Tax deductible amount is the lesser of contribution or limit
  const taxDeductibleAmount = Math.min(contributionAmount, maxAllowableDeduction);

  // Check if at limit and determine which limit applies
  const isAtPercentageLimit = contributionAmount >= maxPercentageLimit;
  const isAtAmountLimit = contributionAmount >= PENSION_LIMITS.maxAnnualAmount;
  const isAtLimit = isAtPercentageLimit || isAtAmountLimit;

  let limitType: 'percentage' | 'amount' | undefined;
  if (isAtLimit) {
    limitType = isAtAmountLimit ? 'amount' : 'percentage';
  }

  // Calculate remaining capacity
  const remainingCapacity = Math.max(0, maxAllowableDeduction - taxDeductibleAmount);

  return {
    contributionAmount,
    taxDeductibleAmount,
    isAtLimit,
    limitType,
    remainingCapacity,
  };
}

/**
 * Validate pension contribution options
 * @param grossIncome Annual gross income
 * @param options Pension options to validate
 * @returns Validation result
 */
export function validatePensionOptions(
  grossIncome: number,
  options: PensionOptions
): {
  isValid: boolean;
  message?: string;
} {
  if (!options.hasPension) {
    return { isValid: true };
  }

  // Validate contribution amount
  if (options.contributionAmount < 0) {
    return {
      isValid: false,
      message: 'Pension contribution cannot be negative',
    };
  }

  if (options.contributionType === 'percentage') {
    if (options.contributionAmount > 100) {
      return {
        isValid: false,
        message: 'Pension contribution percentage cannot exceed 100%',
      };
    }
  } else {
    // Fixed amount validation
    if (options.contributionAmount > grossIncome) {
      return {
        isValid: false,
        message: 'Pension contribution cannot exceed gross income',
      };
    }

    if (options.contributionAmount > PENSION_LIMITS.maxAnnualAmount * 2) {
      return {
        isValid: false,
        message: `Pension contribution seems unusually high (above R${(PENSION_LIMITS.maxAnnualAmount * 2).toLocaleString()})`,
      };
    }
  }

  return { isValid: true };
}